import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@gn-audio/jss-components';

import { NavigationButtons } from 'composables/HearingTest/components/NavigationButtons';
import { StepContainer } from 'composables/HearingTest/components/StepContainer';

import { useHearingTest, useHearingTestTracking } from '../HearingTestContext';
import { translations } from '../translations/en';
import { STEP_NAMES, TOTAL_STEPS } from '../types/HearingTestTypes';
import { HearingTestEventAction } from '../types/TrackingTypes';

export const SoundCheckStep: React.FC = () => {
  const { state, goToStep } = useHearingTest();
  const { trackHearingTestEvent } = useHearingTestTracking();
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    trackHearingTestEvent(HearingTestEventAction.LoadPage, 'How it works');
  }, [trackHearingTestEvent]);

  useEffect(() => {
    audioRef.current = new Audio('/audio/hearing-test/sound-check.mp3');

    const handleEnded = () => setIsPlaying(false);
    audioRef.current.addEventListener('ended', handleEnded);

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', handleEnded);
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  const handleNext = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    goToStep(STEP_NAMES.LEFT_EAR_INTRO);
  };

  const handlePlaySound = async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        trackHearingTestEvent(HearingTestEventAction.Click, 'Pause Sound Demo');
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
      } else {
        trackHearingTestEvent(HearingTestEventAction.Click, 'Play Sound Demo');
        await audioRef.current.play();
        setIsPlaying(true);
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to play audio'));
    }
  };

  return (
    <StepContainer currentStep={state.currentStep} totalSteps={TOTAL_STEPS}>
      <div className="mb-8">
        <h1 className="mb-4 text-2xl font-bold text-gray-900">{translations.soundcheck}</h1>
        <p className="mb-2 text-lg font-semibold text-gray-900">{translations.headphoneson}</p>
        <p className="text-gray-700">{translations.bgnoises}</p>
      </div>

      <div className="mb-8">
        <h2 className="mb-6 text-xl font-bold text-gray-900">Instructions</h2>

        <div className="space-y-4">
          <div className="flex items-start">
            <div className="mr-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-on-brand">
              1
            </div>
            <p className="pt-1 text-gray-900">{translations.setyourdevicemax}</p>
          </div>

          <div className="flex items-start">
            <div className="mr-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-on-brand">
              2
            </div>
            <p className="pt-1 text-gray-900">{translations.testheadphones}</p>
          </div>

          <div className="pl-12">
            <Button onClick={handlePlaySound} disabled={!!error} variant="primary" size="l">
              {isPlaying ? translations.stopsong : translations.playsong}
            </Button>

            {error && (
              <div className="bg-red-50 text-red-700 mt-4 rounded-lg p-4">
                <p className="text-sm">{error.message}</p>
              </div>
            )}
          </div>

          <div className="flex items-start">
            <div className="mr-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-on-brand">
              3
            </div>
            <p className="pt-1 text-gray-900">{translations.keepvolume}</p>
          </div>
        </div>
      </div>

      <NavigationButtons onNext={handleNext} />
    </StepContainer>
  );
};
